<script setup lang="ts">
import { useTooltip } from '@/sharedComposables/useTooltip'
import HotKey from '@/uiKit/HotKey.vue'
import ToolTip from '@/uiKit/ToolTip.vue'
import { computed, ref, useId } from 'vue'

const props = withDefaults(
  defineProps<{
    disableTooltip?: boolean
    isOpen: boolean
    side?: 'left' | 'right'
    openTooltip: string
    closeTooltip: string
    hotkey?: string
    label?: string
  }>(),
  { side: 'left', hotkey: undefined, label: undefined },
)
const emit = defineEmits<{ (e: 'toggle'): void }>()

// We use this to avoid lingering tooltips during
// the toggle animation.
// https://www.loom.com/share/6ac21f00be7d4cf5bce6da6089d49833?sid=88548566-09a7-422c-bbf9-4c0e87351b93
// Effectively, before emiting toggle, we close the tooltip first, then emit toggle on next tick
// There used to be a different implementation relying on useDebounce and watchDebounced, but it was
// causing issues with the tooltip when navigating between views.
const id = useId()

const tempDisableTooltip = ref(false)

const {
  isOpen: tooltipOpen,
  trigger,
  popover,
} = useTooltip({ disabled: computed(() => props.disableTooltip) })
</script>

<template>
  <button
    v-bind="$attrs"
    :id="id"
    class="group/svg flex size-7 select-none flex-col items-center justify-center rounded-full border-icon-subtle bg-background-transparent text-icon-subtle transition-colors data-[state=open]:bg-background-transparent-hovered hover:bg-background-transparent-hovered focus:bg-surface-secondary focus:text-icon-subtle focus:outline-none focus-visible:bg-background-transparent-pressed focus-visible:shadow-focus-ring-primary active:bg-background-transparent-pressed"
    :aria-label="label || (isOpen ? closeTooltip : openTooltip)"
    :style="side === 'right' ? { transform: 'scaleX(-1)' } : undefined"
    @click="
      async () => {
        tooltipOpen = false
        tempDisableTooltip = false
        emit('toggle')
      }
    "
    @mouseenter="trigger.onMouseOver"
    @mouseleave="trigger.onMouseLeave"
  >
    <svg
      viewBox="0 0 10.67 10.67"
      class="size-[12px]"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M2.37101 4.75794e-07H8.30398C8.61309 -9.52421e-06 8.87629 -1.95298e-05 9.09239 0.0176405C9.31939 0.0361805 9.54019 0.0768004 9.75129 0.18438C10.0696 0.34656 10.3284 0.605361 10.4906 0.923671C10.5982 1.13479 10.6388 1.35564 10.6574 1.58265C10.675 1.79874 10.675 2.06193 10.675 2.37103V8.30396C10.675 8.6131 10.675 8.8763 10.6574 9.0923C10.6388 9.3194 10.5982 9.5402 10.4906 9.7513C10.3284 10.0696 10.0696 10.3284 9.75129 10.4906C9.54019 10.5982 9.31939 10.6388 9.09239 10.6574C8.87629 10.675 8.61309 10.675 8.30399 10.675H2.371C2.06191 10.675 1.79873 10.675 1.58265 10.6574C1.35564 10.6388 1.13479 10.5982 0.92366 10.4906C0.60536 10.3284 0.34656 10.0696 0.18438 9.7513C0.0768004 9.5402 0.0361805 9.3194 0.0176405 9.0923C-1.95298e-05 8.8763 -9.52421e-06 8.6131 4.75794e-07 8.30398V2.37101C-9.52421e-06 2.06192 -1.95298e-05 1.79873 0.0176405 1.58265C0.0361805 1.35564 0.0768004 1.13479 0.18438 0.923661C0.34656 0.605361 0.60536 0.34656 0.92366 0.18438C1.13479 0.0768004 1.35564 0.0361805 1.58265 0.0176405C1.79873 -1.95298e-05 2.06192 -9.52421e-06 2.37101 4.75794e-07ZM1.66815 1.06415C1.50607 1.07739 1.43879 1.10035 1.40035 1.11994C1.27962 1.18145 1.18145 1.27962 1.11993 1.40035C1.10035 1.43879 1.07739 1.50607 1.06415 1.66815C1.05041 1.83636 1.05 2.0563 1.05 2.39167V8.28333C1.05 8.6187 1.05041 8.8386 1.06415 9.0068C1.07739 9.1689 1.10035 9.2362 1.11993 9.2746C1.18145 9.3954 1.27962 9.4935 1.40035 9.5551C1.43879 9.5746 1.50607 9.5976 1.66815 9.6108C1.83636 9.6246 2.0563 9.625 2.39166 9.625H8.28333C8.61869 9.625 8.83859 9.6246 9.00689 9.6108C9.16889 9.5976 9.23619 9.5746 9.27469 9.5551C9.39539 9.4935 9.49359 9.3954 9.55509 9.2746C9.57469 9.2362 9.59759 9.1689 9.61089 9.0068C9.62459 8.8386 9.62499 8.6187 9.62499 8.28333V2.39167C9.62499 2.0563 9.62459 1.83636 9.61089 1.66815C9.59759 1.50607 9.57469 1.43879 9.55509 1.40035C9.49359 1.27962 9.39539 1.18145 9.27469 1.11994C9.23619 1.10035 9.16889 1.07739 9.00689 1.06415C8.83859 1.05041 8.61869 1.05 8.28333 1.05H2.39166C2.0563 1.05 1.83636 1.05041 1.66815 1.06415Z"
        fill="currentColor"
      />
      <path
        class="transition duration-300 ease-in-out-quint group-hover/svg:-translate-x-3"
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M2.85832 2.33325C3.14827 2.33325 3.38332 2.5683 3.38332 2.85825V7.81658C3.38332 8.10653 3.14827 8.3416 2.85832 8.3416C2.56837 8.3416 2.33332 8.10653 2.33332 7.81658V2.85825C2.33332 2.5683 2.56837 2.33325 2.85832 2.33325Z"
        fill="currentColor"
      />

      <path
        v-if="(isOpen && props.side === 'left') || (!isOpen && props.side === 'right')"
        class="translate-x-3 transition duration-300 ease-in-out-quint group-hover/svg:translate-x-0"
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M5.19105 2.98385C5.38631 3.17911 5.38631 3.49569 5.19105 3.69096L4.0446 4.8374H7.83749C8.11364 4.8374 8.33749 5.06126 8.33749 5.3374C8.33749 5.61354 8.11364 5.8374 7.83749 5.8374H4.0446L5.19105 6.98385C5.38631 7.17911 5.38631 7.49569 5.19105 7.69096C4.99579 7.88622 4.6792 7.88622 4.48394 7.69096L2.48394 5.69096C2.28868 5.49569 2.28868 5.17911 2.48394 4.98385L4.48394 2.98385C4.6792 2.78859 4.99579 2.78859 5.19105 2.98385Z"
        fill="currentColor"
      />
      <path
        v-if="(!isOpen && props.side === 'left') || (isOpen && props.side === 'right')"
        class="-translate-x-3 transition duration-300 ease-in-out-quint group-hover/svg:translate-x-0"
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M5.48394 2.98385C5.28868 3.17911 5.28868 3.49569 5.48394 3.69096L6.63039 4.8374H2.83749C2.56135 4.8374 2.33749 5.06126 2.33749 5.3374C2.33749 5.61354 2.56135 5.8374 2.83749 5.8374H6.63039L5.48394 6.98385C5.28868 7.17911 5.28868 7.49569 5.48394 7.69096C5.6792 7.88622 5.99578 7.88622 6.19105 7.69096L8.19105 5.69096C8.38631 5.49569 8.38631 5.17911 8.19105 4.98385L6.19105 2.98385C5.99578 2.78859 5.6792 2.78859 5.48394 2.98385Z"
        fill="currentColor"
      />
    </svg>
    <ToolTip
      v-if="tooltipOpen"
      :open="true"
      :placement="{ allowedPlacements: [side === 'left' ? 'right' : 'left'] }"
      :target-selector="`#${id}`"
      :title="'Foo'"
    >
      <template #content>
        <div
          class="flex items-center gap-1.5"
          @mouseenter="popover.onMouseOver"
          @mouseleave="popover.onMouseLeave"
        >
          <span>{{ isOpen ? closeTooltip : openTooltip }}</span>
          <HotKey
            v-if="hotkey"
            hotkey="BracketLeft"
            data-theme="dark"
            size="xs"
          />
        </div>
      </template>
    </ToolTip>
  </button>
</template>
